<template>
  <div class="flex flex-col gap-4">
    <InputText
      name="ivp-cvr"
      id="ivp-cvr"
      type="text"
      required
      maxlength="10"
      :placeholder="$t('checkout.ivp_cvr_placeholder')"
      :rules="rules.ivp_cvr"
      v-model="checkoutStore.ivpFormData.ivp_cvr"
    />

    <InputText
      name="ivp-name"
      id="ivp-name"
      type="text"
      required
      :placeholder="$t('checkout.ivp_name_placeholder')"
      :rules="rules.ivp_name"
      v-model="checkoutStore.ivpFormData.ivp_name"
    />

    <InputText
      name="ivp-attperson"
      id="ivp-attperson"
      type="text"
      required
      :placeholder="$t('checkout.ivp_attperson_placeholder')"
      :rules="rules.ivp_attperson"
      v-model="checkoutStore.ivpFormData.ivp_attperson"
    />

    <InputText
      name="ivp-address"
      id="ivp-address"
      type="text"
      required
      :placeholder="$t('checkout.ivp_address_placeholder')"
      :rules="rules.ivp_address"
      v-model="checkoutStore.ivpFormData.ivp_address"
    />

    <div class="flex gap-4">
      <InputText
        id="ivp-zip"
        class="w-32"
        name="ivp-zip"
        type="text"
        required
        :placeholder="$t('checkout.ivp_zip_placeholder')"
        :rules="rules.ivp_zip"
        v-model="checkoutStore.ivpFormData.ivp_zip"
      />
      <InputText
        id="ivp-city"
        class="flex-1"
        name="ivp-city"
        type="text"
        required
        :placeholder="$t('checkout.ivp_city_placeholder')"
        :rules="rules.ivp_city"
        v-model="checkoutStore.ivpFormData.ivp_city"
      />
    </div>

    <InputText
      name="ivp-email"
      id="ivp-email"
      type="email"
      :required="checkoutStore.ivpEmailRequired"
      :placeholder="$t('checkout.ivp_email_placeholder')"
      :rules="
        checkoutStore.ivpEmailRequired
          ? rules.ivp_email_required
          : rules.ivp_email_optional
      "
      v-model="checkoutStore.ivpFormData.ivp_email"
    />

    <InputText
      name="ivp-email2"
      id="ivp-email2"
      type="email"
      :required="emailRepeatRequired"
      :placeholder="$t('checkout.ivp_repeat_email_placeholder')"
      :rules="
        emailRepeatRequired
          ? rules.ivp_email_repeat_required
          : rules.ivp_email_repeat_optional
      "
      v-model="checkoutStore.ivpFormData.ivp_email2"
    />

    <InputText
      name="ivp-ean"
      id="ivp-ean"
      type="text"
      :required="checkoutStore.ivpEanRequired"
      maxlength="13"
      :placeholder="$t('checkout.ivp_ean_placeholder')"
      :rules="
        checkoutStore.ivpEanRequired
          ? rules.ivp_ean_required
          : rules.ivp_ean_optional
      "
      v-model="checkoutStore.ivpFormData.ivp_ean"
    />

    <InputText
      name="ivp-reference"
      id="ivp-reference"
      type="text"
      :placeholder="$t('checkout.ivp_reference_placeholder')"
      v-model="checkoutStore.ivpFormData.ivp_reference"
    />

    <InputCheckbox
      class="mt-4"
      checked-border-class="border-accent"
      checked-checkmark-class="text-accent"
      id="ivp-terms"
      name="ivp-terms"
      v-model="checkoutStore.ivpFormData.ivp_toa"
      :required="true"
      :rules="rules.ivp_toa"
    >
      {{ $t('checkout.ivp_terms_label') }}
    </InputCheckbox>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import * as yup from 'yup';

  import InputCheckbox from '@/components/ui/inputs/InputCheckbox.vue';
  import InputText from '@/components/ui/inputs/InputText.vue';
  import { useCheckoutStore } from '@/stores/checkoutStore';

  const { t } = useI18n();
  const checkoutStore = useCheckoutStore();

  const emailRepeatRequired = computed(
    () =>
      checkoutStore.ivpEmailRequired || !!checkoutStore.ivpFormData.ivp_email
  );

  const emailsMatch = (value: string | null | undefined) =>
    (value ?? '') === (checkoutStore.ivpFormData.ivp_email ?? '');

  const rules = {
    ivp_name: yup.string().required(t('validation.required')),
    ivp_attperson: yup.string().required(t('validation.required')),
    ivp_address: yup.string().required(t('validation.required')),
    ivp_zip: yup.string().required(t('validation.required')),
    ivp_city: yup.string().required(t('validation.required')),
    ivp_email_required: yup
      .string()
      .email(t('validation.email'))
      .required(t('validation.required')),
    ivp_email_optional: yup.string().email(t('validation.email')).notRequired(),
    ivp_email_repeat_required: yup
      .string()
      .email(t('validation.email'))
      .required(t('validation.required'))
      .test('emails-match', t('validation.emails_must_match'), emailsMatch),
    ivp_email_repeat_optional: yup
      .string()
      .email(t('validation.email'))
      .notRequired()
      .test('emails-match', t('validation.emails_must_match'), emailsMatch),
    ivp_ean_required: yup
      .string()
      .matches(/^\d{13}$/, t('validation.exact_length', { length: 13 }))
      .required(t('validation.required')),
    ivp_ean_optional: yup
      .string()
      .notRequired()
      .test(
        'ean-format',
        t('validation.exact_length', { length: 13 }),
        value => !value || /^\d{13}$/.test(value)
      ),
    ivp_cvr: yup
      .string()
      .matches(/^\d+$/, t('validation.number'))
      .max(10, t('validation.char_limit', { limit: 10 }))
      .required(t('validation.required')),
    ivp_toa: yup
      .boolean()
      .isTrue(t('validation.terms'))
      .required(t('validation.terms')),
  };
</script>
